import type { BufferedChangeset } from 'ember-changeset/types'

export const CategoryGroup = {
  fiction: 'fiction',
  nonfiction: 'nonfiction',
  ancillary: 'ancillary',
} as const

export type CategoryGroupType = keyof typeof CategoryGroup

export type FormChangeset = BufferedChangeset & Record<CategoryGroupType, string[]>

export const CategoryGroupDefaultOrder: readonly (typeof CategoryGroup.fiction | typeof CategoryGroup.nonfiction)[] = [
  CategoryGroup.fiction,
  CategoryGroup.nonfiction,
]

export const CategoriesByGroup = {
  fiction: {
    id: 'fiction',
    categories: [
      'fiction.adventure',
      'fiction.classics',
      'fiction.drama',
      'fiction.fairytales_and_myths',
      'fiction.fantasy',
      'fiction.funny',
      'fiction.mystery',
      'fiction.poetry',
      'fiction.realistic',
      'fiction.science_fiction',
    ],
  },
  nonfiction: {
    id: 'nonfiction',
    categories: [
      'nonfiction.biography',
      'nonfiction.creative_arts',
      'nonfiction.design_and_technology',
      'nonfiction.earth_science',
      'nonfiction.geography',
      'nonfiction.health_and_fitness',
      'nonfiction.history',
      'nonfiction.life_science',
      'nonfiction.mathematics',
      'nonfiction.people_and_society',
      'nonfiction.physical_science',
      'nonfiction.work',
    ],
  },
}

/**
 * If selectedIds contains all availableIds returns the singleId that represents all availableIds.
 * @example
 *
 * coalesceAllIdsToSingleId('fiction.*', ['fiction.adventure', 'fiction.drama'], ['fiction.adventure', 'fiction.drama'])
 *
 * > ['fiction.*']
 *
 * coalesceAllIdsToSingleId('fiction.*', ['fiction.adventure'], ['fiction.adventure', 'fiction.drama'])
 *
 * > ['fiction.adventure']
 */
export function coalesceAllIdsToSingleId(singleId: string, selectedIds: string[], availableIds: string[]): string[] {
  const hasAllIds = availableIds.every((availableId) => selectedIds.includes(availableId))
  return hasAllIds ? [singleId] : selectedIds
}

/**
 * If selectedIds contains singleId, replace singleId with availableIds.
 * @example
 *
 * decoalesceSingleIdToAllIds('fiction.*', ['fiction.*', 'nonfiction.geography'], ['fiction.adventure', 'fiction.drama'])
 *
 * > ['fiction.adventure', 'fiction.drama', 'nonfiction.geography']
 *
 * decoalesceAllIdsToSingleId('fiction.*', ['fiction.adventure'], ['fiction.adventure', 'fiction.drama'])
 *
 * > ['fiction.adventure']
 */
export function decoalesceSingleIdToAllIds(singleId: string, selectedIds: string[], availableIds: string[]): string[] {
  return selectedIds.flatMap((selectedId) => (singleId === selectedId ? availableIds : [selectedId]))
}

/**
 * Calls coalesceAllIdsToSingleId() for each categoryFilterType.
 * Used to convert data that does not contain coalesced ids such as checkbox
 * state e.g. ['fiction.adventure', 'fiction.drama', ...] to query params like
 * ['fiction.*'] (sent to the server).
 */
export function coalesceCategoryIds(categoryIds: string[]): string[] {
  return CategoryGroupDefaultOrder.reduce((coalescedIds, categoryFilterType) => {
    const availableIds = CategoriesByGroup[categoryFilterType].categories
    const categoriesForCurrentFilterType = coalescedIds.filter((category) =>
      category.startsWith(`${categoryFilterType}.`),
    )
    const categoriesForOtherFilterType = coalescedIds.filter(
      (category) => !category.startsWith(`${categoryFilterType}.`),
    )
    return [
      ...categoriesForOtherFilterType,
      ...coalesceAllIdsToSingleId(`${categoryFilterType}.*`, categoriesForCurrentFilterType, availableIds),
    ]
  }, categoryIds)
}

/**
 * Calls decoalesceSingleIdToAllIds() for each classification id.
 * Used to convert query params containing coalesced ids like ['fiction.*']
 * (sent to the server) to data without coalesced ids e.g.
 * ['fiction.adventure', 'fiction.drama', ...] (for driving checkbox state).
 */
export function decoalesceCategoryIds(categoryIds: string[]): string[] {
  if (!Array.isArray(categoryIds)) return []
  return CategoryGroupDefaultOrder.reduce((allIds, categoryFilterType) => {
    const availableIds = CategoriesByGroup[categoryFilterType].categories
    return decoalesceSingleIdToAllIds(`${categoryFilterType}.*`, allIds, availableIds)
  }, categoryIds)
}
